import { useCallback, useContext, useId, type ChangeEvent, type ReactNode, type Ref } from 'react'

import { cn } from './cn'
import { FieldError } from './FieldError'
import { FieldLabelContext } from './field-label'
import { Check, Info } from './icons'

export interface CheckboxProps {
  readonly checked: boolean
  readonly onChange: (checked: boolean) => void
  /** Left out (or empty) for a box in a `FormRow`, whose label then names it. */
  readonly label?: ReactNode
  /** Follows the row's label when a `FormRow` holds several fields. */
  readonly labelSuffix?: string
  /** Shows the small info icon some legacy labels carry. */
  readonly info?: boolean
  /** The error message; when set, the box turns error and the checkbox names the message. */
  readonly error?: string
  /** Marks the box invalid without a message of its own, as one of a group that shows its message once. */
  readonly invalid?: boolean
  /** The id of a hint that describes the box, read before any error. */
  readonly describedBy?: string
  /** Goes on the outer element, which holds the checkbox and any error. */
  readonly className?: string
  /** For a form library: the field's name, blur handler and the input's ref. */
  readonly name?: string
  readonly onBlur?: () => void
  readonly inputRef?: Ref<HTMLInputElement>
}

/**
 * A real checkbox, visually hidden, behind a drawn box: primary fill and a check once checked.
 * The box shows the input's keyboard focus.
 */
export function Checkbox({
  checked,
  onChange,
  label,
  labelSuffix,
  info = false,
  error,
  invalid = false,
  describedBy,
  className,
  name,
  onBlur,
  inputRef,
}: CheckboxProps) {
  const errorId = useId()
  const suffixId = useId()
  const rowLabelId = useContext(FieldLabelContext)
  const hasLabel = label !== undefined && label !== null && label !== '' && label !== false
  // Its own label first; else the row's label, then its suffix.
  const suffixed = !hasLabel && rowLabelId !== null && labelSuffix !== undefined && labelSuffix !== ''
  const labelledBy = hasLabel || rowLabelId === null ? undefined : suffixed ? `${rowLabelId} ${suffixId}` : rowLabelId
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked),
    [onChange],
  )

  return (
    <div className={cn('inline-flex flex-col gap-1', className)}>
      <label className="inline-flex min-h-6 min-w-6 cursor-pointer items-center gap-2 text-sm text-text-primary">
        <input
          type="checkbox"
          ref={inputRef}
          name={name}
          checked={checked}
          onChange={handleChange}
          onBlur={onBlur}
          aria-labelledby={labelledBy}
          aria-invalid={error !== undefined || invalid || undefined}
          aria-describedby={[describedBy, error === undefined ? undefined : errorId].filter(Boolean).join(' ') || undefined}
          className="peer sr-only"
        />
        <span
          className={cn(
            'flex h-4 w-4 shrink-0 items-center justify-center rounded-xs border transition',
            'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary-main',
            checked
              ? 'border-primary-main bg-primary-main text-primary-contrast'
              : error === undefined && !invalid
                ? 'border-text-light bg-background'
                : 'border-error-main bg-background',
          )}
        >
          {checked && <Check size={12} />}
        </span>
        {(hasLabel || info) && (
          <span className="inline-flex items-center gap-1.5">
            {hasLabel && label}
            {info && <Info size={14} className="shrink-0 text-text-light" />}
          </span>
        )}
        {suffixed && (
          <span id={suffixId} className="sr-only">
            {labelSuffix}
          </span>
        )}
      </label>
      {error !== undefined && <FieldError id={errorId} message={error} />}
    </div>
  )
}
